// External libraries
import React from 'react';

// Internal modules
import { appStore } from '@/store';
import { $logger, features, getContent, getLocale } from '@/lib';
import { FeeDisclosure } from './Components';
import { getCurrencyConversionData } from './api';
import { isConversionNeeded as getIsConversionNeeded } from '../../utils';
import { PREPARE_CURRENCY_CONVERSION_PAYLOAD } from './fpti';
import {
  BUYER_FX_INELIGIBLE_COUNTRIES,
  FORCED_PAYPAL_CONVERSION_CARD_BRANDS,
  FORCED_VENDOR_CONVERSION_CARD_BRANDS,
} from './constants';

/**
 * @function isBuyerFxEligible
 * @returns {boolean}
 * @description Checks for Buyer FX eligibility based on:
 * 1. Country eligibility
 * 2. Card type eligibility
 * 3. Domestic transactions restricted to domestic currency (ex: IN)
 */
const isBuyerFxEligible = () => {
  const { api: apiState, form: formState } = appStore.getState();
  const type = formState.cardNumber.cardType || '';
  const { country: merchantCountry } = apiState.cartDetails.merchant || {};
  const { country: buyerCountry } = getLocale();
  const isDomesticTransaction = buyerCountry === merchantCountry;
  const isRestrictedDomesticTransaction =
    features.isActive('hasDccDomesticRestriction') && isDomesticTransaction;

  return (
    !BUYER_FX_INELIGIBLE_COUNTRIES.includes(buyerCountry) &&
    !FORCED_PAYPAL_CONVERSION_CARD_BRANDS.includes(type) &&
    !FORCED_VENDOR_CONVERSION_CARD_BRANDS.includes(type) &&
    !isRestrictedDomesticTransaction
  );
};

const conditionallyClearCardConversion = () => {
  const { ui: uiState } = appStore.getState();

  /**
   * If conversion options are shown:
   * 1. Hide/unmount the conversion options
   * 2. Reset the conversion selection
   * 3. Reset the conversion data
   */
  if (uiState.showCardConversionOptions) {
    $logger.info('clear_card_currency_conversion');
    const {
      api: apiActions,
      form: formActions,
      ui: uiActions,
    } = appStore.getActions();

    uiActions.setShowCardConversionOptions(false);
    formActions.updateCurrencyConversionSelection('');
    apiActions.setCurrencyConversionData(null);
  }
};

const conditionallyFetchConversionData = nextFocusedEl => {
  const { form: formState, ui: uiState } = appStore.getState();
  const isConversionNeeded = getIsConversionNeeded();
  const isCardNumberValid = formState.cardNumber.isValid;
  const isConversionShown = uiState.showCardConversionOptions;
  const isDccEligible = isBuyerFxEligible();

  // Return early if we don't need to show the conversion options
  // or if the conversion options are already shown
  if (
    !isConversionNeeded ||
    !isCardNumberValid ||
    isConversionShown ||
    !isDccEligible
  ) {
    $logger.info('skip_fetch_card_conversion_call', {
      isCardNumberValid,
      isConversionNeeded,
      isConversionShown,
      isDccEligible,
    });
    return;
  }

  const { ui: uiActions } = appStore.getActions();
  uiActions.toggleIsFormSubmitting(true);
  $logger.info('fetching_currency_conversion_data');

  // Any errors returned by this API are swallowed since it doesn't
  // block the transaction. That logic is part of `getCurrencyConversionData()`
  getCurrencyConversionData().then(conversionData => {
    uiActions.toggleIsFormSubmitting(false);

    // If an element to focus on after the call is completed
    // has been passed, focus on that element
    nextFocusedEl?.focus?.();

    const payload = PREPARE_CURRENCY_CONVERSION_PAYLOAD(conversionData);
    $logger.fpti('prepare_currency_conversion_options', payload);

    // If the user cannot change the conversion type then we don't
    // display the conversion options
    if (conversionData?.canChangeConversionType) {
      uiActions.setShowCardConversionOptions(true);
    }
  });
};

const getAmlDisclosure = () => {
  return features.isActive('isAmlCountry')
    ? getContent('currencyConversion.paypalDisclosure.aml')
    : null;
};

const getDisclosures = conversionData => {
  const { feeRateFormatted: feeRate, spread: conversionRate } = conversionData;

  return {
    paypal: [
      {
        disclosure: getContent('currencyConversion.paypalDisclosure.rate', {
          conversionRate,
        }),
        isEmphasized: true,
      },
      { disclosure: <FeeDisclosure feeRate={feeRate} /> },
      getAmlDisclosure(),
    ].filter(Boolean),
    vendor: getContent('currencyConversion.vendorDisclosure'),
  };
};

const getIofDisclosure = () => {
  return features.isActive('hasDccIofDisclosure')
    ? { iofDisclosure: getContent('currencyConversion.iofDisclosure') }
    : null;
};

const getTotal = (selected, conversionData) => {
  const { from, to } = conversionData;

  return {
    amount: to.currencyFormatSymbolISOCurrency,
    convertedAmount: from.currencyFormatSymbolISOCurrency,
    label: getContent(`currencyConversion.totalLabel.${selected}`),
  };
};

const isConversionChangeable = () => {
  const { currencyConversionData } = appStore.getState().api;

  // Coercing to a boolean since the value is initially undefined
  return Boolean(currencyConversionData?.canChangeConversionType);
};

export {
  conditionallyClearCardConversion,
  conditionallyFetchConversionData,
  getDisclosures,
  getIofDisclosure,
  getTotal,
  isBuyerFxEligible,
  isConversionChangeable,
};
